import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ChefHat, CloudOff, MapPin, Phone, Printer, RefreshCw, Users,
} from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { ShareButton } from "../components/ShareButton";
import { describeAge, fetchWithCache } from "../lib/offlineCache";
import { formatTime12 } from "../lib/mealTypes";

/**
 * The whole day, across every event.
 *
 * The single-event Today view assumes one thing is happening. This
 * organisation routinely runs five or more at once — sometimes in one venue,
 * often sharing vendors across venues — so a coordinator needs the picture
 * before they need the detail.
 *
 * Two deliberate choices:
 *   - Overlap is normal here, so shared venues and shared vendors are shown as
 *     information. Only genuine overload is flagged, or people would learn to
 *     ignore the warnings entirely.
 *   - This page is designed to be printed. Volunteers work off paper taped to
 *     a wall, and a printed sheet does not run out of battery.
 */

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function DayViewPage() {
  const navigate = useNavigate();
  const { authAxios } = useAuth();
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchWithCache(`day:${date}`, async () => {
        const api = authAxios();
        const res = await api.get(`/schedule/day?date=${date}`);
        return res.data;
      });
      setData(result.data);
      setStale(result.stale ? result.ageMs : null);
    } catch {
      toast.error("Could not load the day");
    } finally {
      setLoading(false);
    }
  }, [authAxios, date]);

  useEffect(() => { load(); }, [load]);

  const shareText = () => {
    if (!data?.events?.length) return `No events on ${date}.`;
    const lines = [`📅 *${date}* — ${data.event_count} events, ${data.total_people} people`, ""];
    for (const venue of data.venues) {
      lines.push(`📍 *${venue.location}*`);
      for (const e of venue.events) {
        lines.push(`• ${e.title}${e.first_meal ? ` (from ${formatTime12(e.first_meal)})` : ""} — ${e.people} people`);
      }
      lines.push("");
    }
    if (data.stretched_vendors.length) {
      lines.push("⚠️ Stretched vendors:");
      for (const v of data.stretched_vendors) {
        lines.push(`• ${v.vendor_name} — ${v.event_count} events`);
      }
    }
    return lines.join("\n").trim();
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8 w-full space-y-3">
        <div className="h-24 bg-muted rounded-xl animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-5 pb-24 print-sheet">
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <div className="flex items-center gap-2">
          <label htmlFor="day-date" className="sr-only">Date</label>
          <Input id="day-date" type="date" value={date} className="h-11 w-44"
                 onChange={(e) => setDate(e.target.value)} data-testid="day-date" />
          <Button variant="ghost" size="sm" onClick={load} aria-label="Refresh"
                  data-testid="day-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <ShareButton getText={shareText} title={`Schedule for ${date}`} testId="day-share" />
          <Button variant="outline" size="sm" className="gap-1.5"
                  onClick={() => window.print()} data-testid="day-print">
            <Printer className="h-4 w-4" />
            Print
          </Button>
        </div>
      </div>

      {stale != null && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 no-print"
             data-testid="day-stale">
          <CloudOff className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
          <p className="text-xs text-amber-700 dark:text-amber-500 leading-relaxed">
            <strong>Showing the last update from {describeAge(stale)}.</strong>{" "}
            You are offline — this may not reflect changes made since.
          </p>
        </div>
      )}

      <header>
        <h1 className="text-2xl font-bold text-foreground">{date}</h1>
        <p className="text-sm text-muted-foreground">
          {data.event_count} event{data.event_count === 1 ? "" : "s"}
          {data.total_people ? ` · ${data.total_people} people expected` : ""}
        </p>
      </header>

      {data.event_count === 0 && (
        <Card>
          <CardContent className="p-8 text-center" data-testid="day-empty">
            <p className="text-sm text-muted-foreground">Nothing scheduled on this date.</p>
          </CardContent>
        </Card>
      )}

      {/* Only genuine overload is escalated. Sharing a vendor is a normal
          Saturday here and must not look like an alarm. */}
      {data.stretched_vendors?.length > 0 && (
        <section aria-labelledby="stretched-heading" data-testid="stretched-vendors">
          <Card className="border-destructive/40 bg-destructive/5">
            <CardContent className="p-4 space-y-2">
              <h2 id="stretched-heading"
                  className="font-semibold text-destructive flex items-center gap-1.5 text-sm">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                Vendors stretched thin
              </h2>
              {data.stretched_vendors.map((v) => (
                <p key={v.vendor_id} className="text-sm text-foreground">
                  <strong>{v.vendor_name}</strong> — {v.event_count} events today
                  {v.capacity ? `, usual limit ${v.capacity}` : ""}
                  {v.contact_phone && (
                    <a href={`tel:${v.contact_phone}`}
                       className="ml-2 text-primary underline no-print">Call</a>
                  )}
                </p>
              ))}
            </CardContent>
          </Card>
        </section>
      )}

      {data.venues?.map((venue) => (
        <section key={venue.location} aria-labelledby={`venue-${venue.location}`}
                 data-testid={`venue-${venue.location}`}>
          <h2 id={`venue-${venue.location}`}
              className="font-semibold text-foreground flex items-center gap-1.5 mb-2">
            <MapPin className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            {venue.location}
            <span className="text-xs font-normal text-muted-foreground">
              · {venue.event_count} event{venue.event_count === 1 ? "" : "s"}
            </span>
          </h2>
          <div className="space-y-2">
            {venue.events.map((e) => (
              <Card key={e.event_id} data-testid={`day-event-${e.event_id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{e.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {e.first_meal
                        ? `${formatTime12(e.first_meal)}–${formatTime12(e.last_meal)}`
                        : "No timed meals"}
                      {" · "}{e.meals} meal{e.meals === 1 ? "" : "s"}
                      {e.people ? ` · ${e.people} people` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {e.status !== "approved" && (
                      <Badge variant="secondary" className="text-xs">{e.status}</Badge>
                    )}
                    <Button size="sm" variant="outline" className="no-print"
                            onClick={() => navigate(`/events/${e.event_id}/today`)}
                            data-testid={`open-${e.event_id}`}>
                      Open
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      ))}

      {data.vendors?.length > 0 && (
        <section aria-labelledby="vendors-heading">
          <h2 id="vendors-heading"
              className="font-semibold text-foreground flex items-center gap-1.5 mb-2">
            <ChefHat className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Vendors today
          </h2>
          <div className="space-y-2" data-testid="day-vendors">
            {data.vendors.map((v) => (
              <Card key={v.vendor_id}
                    className={v.over_capacity ? "border-destructive/40" : ""}>
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{v.vendor_name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {v.events.map((e) => e.title).join(" · ")}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Users className="h-3 w-3" aria-hidden="true" />{v.people}
                    </span>
                    {v.contact_phone && (
                      <a href={`tel:${v.contact_phone}`} className="no-print"
                         aria-label={`Call ${v.vendor_name}`}>
                        <Button size="sm" variant="outline" className="h-10 gap-1.5">
                          <Phone className="h-4 w-4" aria-hidden="true" />
                          Call
                        </Button>
                      </a>
                    )}
                    <span className="hidden print:inline text-xs">{v.contact_phone}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
